import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useGestaoUsuarios } from '../context/GestaoUsuariosProvider';
import { 
  ArrowLeft, 
  Users, 
  Edit, 
  Shield, 
  Settings, 
  Eye as EyeIcon, 
  Mail, 
  Phone, 
  Calendar,
  UserCheck,
  UserX,
  Key,
  Building
} from 'lucide-react';

const DetalhesUsuario = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { obterUsuario, toggleStatusUsuario, redefinirSenha, enviarResetSenha } = useGestaoUsuarios();
  
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUsuario = async () => {
      try {
        const data = await obterUsuario(id);
        setUsuario(data);
      } catch (error) {
        console.error('Erro ao carregar usuário:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUsuario();
  }, [id, obterUsuario]);

  const handleToggleStatus = async () => {
    if (window.confirm(`Tem certeza que deseja ${usuario.ativo ? 'desativar' : 'ativar'} este usuário?`)) {
      await toggleStatusUsuario(id, !usuario.ativo);
      setUsuario(prev => ({ ...prev, ativo: !prev.ativo }));
    }
  };

  const handleEnviarResetSenha = async () => {
    if (window.confirm('Será enviado um email para o usuário redefinir a própria senha. Continuar?')) {
      try {
        await enviarResetSenha(usuario.email);
      } catch (error) {
        console.error('Erro ao enviar reset de senha:', error);
      }
    }
  };

  const getNivelAcessoInfo = (nivel, setor) => {
    // Se tem setor, é colaborador
    if (setor) {
      return {
        icon: <Building size={20} color="#1565c0" />,
        label: 'Colaborador',
        description: 'Sem acesso ao painel web',
        color: '#1565c0'
      };
    }

    switch (nivel) {
      case 'admin':
        return {
          icon: <Shield size={20} color="#dc3545" />,
          label: 'Administrador',
          description: 'Acesso total ao sistema',
          color: '#dc3545'
        };
      case 'operador':
        return {
          icon: <Settings size={20} color="#ffc107" />,
          label: 'Operador',
          description: 'Acesso a módulos específicos',
          color: '#ffc107'
        };
      case 'visualizador':
        return {
          icon: <EyeIcon size={20} color="#28a745" />,
          label: 'Visualizador',
          description: 'Apenas visualização',
          color: '#28a745'
        };
      default:
        return {
          icon: <Users size={20} color="#6c757d" />,
          label: 'Indefinido',
          description: 'Nível não definido',
          color: '#6c757d'
        };
    }
  };

  const modulosDisponiveis = [
    { key: 'obras', label: 'Obras em Vias Públicas', icon: '🚧' },
    { key: 'obras-fixas', label: 'Obras Fixas', icon: '🏗️' },
    { key: 'fossas-septicas', label: 'Fossas Sépticas', icon: '🏠' },
    { key: 'postes-iluminacao', label: 'Postes de Iluminação', icon: '💡' },
    { key: 'controle-demandas', label: 'Controle de Demandas', icon: '📋' },
    { key: 'mapa-integrado', label: 'Mapa Integrado', icon: '🗺️' },
    { key: 'gestao-usuarios', label: 'Gestão de Usuários', icon: '👥' }
  ];

  if (loading) {
    return (
      <div className="gestao-usuarios detalhes-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando detalhes do usuário...</p>
        </div>
      </div>
    );
  }

  if (!usuario) {
    return (
      <div className="gestao-usuarios detalhes-container">
        <div className="empty-state">
          <Users size={48} />
          <h3>Usuário não encontrado</h3>
          <p>O usuário solicitado não foi encontrado.</p>
          <Link to="/gestao-usuarios" className="button primary">
            Voltar à Lista
          </Link>
        </div>
      </div>
    );
  }

  const nivelInfo = getNivelAcessoInfo(usuario.nivel_acesso, usuario.setor);

  return (
    <div className="gestao-usuarios detalhes-container">
      <div className="detalhes-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <button 
            className="back-button"
            onClick={() => navigate('/gestao-usuarios')}
          >
            <ArrowLeft size={20} />
          </button>
          <div className="header-content">
            <h1>
              <Users size={32} />
              Detalhes do Usuário
            </h1>
            <p>Informações completas do usuário {usuario.nome}</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            onClick={handleEnviarResetSenha}
            className="button secondary"
          >
            <Mail size={16} />
            Redefinir Senha por Email
          </button>
          <button
            onClick={handleToggleStatus}
            className={`button ${usuario.ativo ? 'danger' : 'success'}`}
          >
            {usuario.ativo ? (
              <>
                <UserX size={16} />
                Desativar
              </>
            ) : (
              <>
                <UserCheck size={16} />
                Ativar
              </>
            )}
          </button>
          <Link
            to={`/gestao-usuarios/editar/${usuario.id}`}
            className="button primary"
          >
            <Edit size={16} />
            Editar
          </Link>
        </div>
      </div>

      <div className="detalhes-content">
        <div className="detalhes-grid">
          {/* Informações Básicas */}
          <div className="detalhes-section">
            <h3>Informações Básicas</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Nome Completo</label>
                <div className="info-value">{usuario.nome}</div>
              </div>
              
              <div className="info-item">
                <label>Email</label>
                <div className="info-value">
                  <Mail size={16} />
                  {usuario.email}
                </div>
              </div>
              
              {usuario.celular && (
                <div className="info-item">
                  <label>Telefone</label>
                  <div className="info-value">
                    <Phone size={16} />
                    {usuario.celular}
                  </div>
                </div>
              )}
              
              <div className="info-item">
                <label>Status</label>
                <div className="info-value">
                  <span className={`status-badge ${usuario.ativo ? 'status-ativo' : 'status-inativo'}`}>
                    {usuario.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Nível de Acesso */}
          <div className="detalhes-section">
            <h3>Nível de Acesso</h3>
            <div className="access-level-display">
              <div className="access-level-icon" style={{ backgroundColor: nivelInfo.color }}>
                {nivelInfo.icon}
              </div>
              <div className="access-level-info">
                <h4>{nivelInfo.label}</h4>
                <p>{nivelInfo.description}</p>
              </div>
            </div>
          </div>

          {/* Permissões */}
          <div className="detalhes-section full-width">
            <h3>Permissões por Módulo</h3>
            <div className="permissions-display">
              {modulosDisponiveis.map(modulo => {
                const temPermissao = usuario.permissoes?.[modulo.key] || false;
                return (
                  <div key={modulo.key} className="permission-display-item">
                    <div className="permission-info">
                      <span className="permission-icon">{modulo.icon}</span>
                      <span className="permission-label">{modulo.label}</span>
                    </div>
                    <div className={`permission-status ${temPermissao ? 'granted' : 'denied'}`}>
                      {temPermissao ? '✓ Permitido' : '✗ Negado'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Informações de Sistema */}
          <div className="detalhes-section">
            <h3>Informações do Sistema</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Cadastrado em</label>
                <div className="info-value">
                  <Calendar size={16} />
                  {new Date(usuario.created_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
              
              <div className="info-item">
                <label>Última atualização</label>
                <div className="info-value">
                  <Calendar size={16} />
                  {new Date(usuario.updated_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
              
              <div className="info-item">
                <label>Último acesso</label>
                <div className="info-value">
                  <Calendar size={16} />
                  Não disponível
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default DetalhesUsuario;